import { useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiClose } from '@mdi/js'
import { AppIcon } from './ui/icon'
import {
    dismissNotification,
    getNotifications,
    subscribeToNotifications,
} from '../lib/notifications'
import { Alert, AlertDescription, AlertTitle } from './ui/alert'
import { Button } from './ui/button'

/**
 * Renders the global notification bus (src/lib/notifications.ts) as
 * non-blocking inline banners in the same slot as the auth-error banner —
 * above the header, full width, pushing content down rather than covering it.
 * Deliberately not a toast (front/CLAUDE.md, patterns.md §9).
 */
export function AppNotifications() {
    const notifications = useSyncExternalStore(
        subscribeToNotifications,
        getNotifications,
        getNotifications
    )
    const { t } = useTranslation()

    if (notifications.length === 0) return null

    return (
        <div className="w-full" data-testid="app-notifications">
            {notifications.map((n) => (
                <Alert
                    key={n.id}
                    variant={n.variant}
                    className="rounded-none border-x-0 border-t-0 flex items-start justify-between gap-3"
                    data-testid={`app-notification-${n.key}`}
                >
                    <div className="min-w-0">
                        <AlertTitle>{t(n.titleKey, n.values)}</AlertTitle>
                        {n.descriptionKey && (
                            <AlertDescription>
                                {t(n.descriptionKey, n.values)}
                            </AlertDescription>
                        )}
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() => dismissNotification(n.id)}
                        aria-label={t('actions.close', { defaultValue: 'Close' })}
                        title={t('actions.close', { defaultValue: 'Close' })}
                        data-testid={`app-notification-dismiss-${n.key}`}
                    >
                        <AppIcon path={mdiClose} size="sm" />
                    </Button>
                </Alert>
            ))}
        </div>
    )
}
